import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import { useTrustedIssuers, useSchemaIssuerMapping, useComplianceVCs } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';

interface TrustedIssuer {
  did: string;
  name: string;
  type: string;
  trustScore: number;
  schemas: string[];
  addedAt: string;
}

const typeIcons: Record<string, string> = {
  Government: 'account_balance',
  Financial: 'payments',
  Healthcare: 'health_and_safety',
  Corporate: 'construction',
};

const typeBg: Record<string, string> = {
  Government: 'bg-blue-50',
  Financial: 'bg-emerald-50',
  Healthcare: 'bg-purple-50',
  Corporate: 'bg-orange-50',
};

const typeColor: Record<string, string> = {
  Government: 'text-primary',
  Financial: 'text-emerald-600',
  Healthcare: 'text-purple-600',
  Corporate: 'text-orange-600',
};

export default function TrustFramework() {
  const { data: fetchedIssuers, loading: issuersLoading, error: issuersError, refetch: refetchIssuers } = useTrustedIssuers();
  const { data: schemaIssuerMapping, loading: mappingLoading } = useSchemaIssuerMapping();
  const { data: complianceVCs, loading: complianceLoading } = useComplianceVCs();
  const { t } = useTranslation(['verifier', 'common']);

  const [issuers, setIssuers] = useState<TrustedIssuer[]>([]);
  const [minTrustScore, setMinTrustScore] = useState(70);
  const [showAddIssuer, setShowAddIssuer] = useState(false);
  const [newIssuerDid, setNewIssuerDid] = useState('');
  const [newIssuerName, setNewIssuerName] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (fetchedIssuers) {
      setIssuers(fetchedIssuers);
    }
  }, [fetchedIssuers]);

  const removeIssuer = async (did: string) => {
    try {
      await apiFetch(`/verifier/trusted-issuers/${encodePathSegment(did)}`, { method: 'DELETE' });
      setIssuers(issuers.filter((i) => i.did !== did));
    } catch {
      setIssuers(issuers.filter((i) => i.did !== did));
    }
  };

  const handleAddIssuer = async () => {
    if (!newIssuerDid || !newIssuerName) return;
    setAdding(true);
    try {
      await apiFetch('/verifier/trusted-issuers', {
        method: 'POST',
        body: JSON.stringify({ did: newIssuerDid, name: newIssuerName }),
      });
      refetchIssuers();
      setShowAddIssuer(false);
      setNewIssuerDid('');
      setNewIssuerName('');
    } catch {
      setIssuers([
        ...issuers,
        { did: newIssuerDid, name: newIssuerName, type: 'Corporate', trustScore: 75, schemas: [], addedAt: new Date().toISOString().split('T')[0] },
      ]);
      setShowAddIssuer(false);
      setNewIssuerDid('');
      setNewIssuerName('');
    } finally {
      setAdding(false);
    }
  };

  const loading = issuersLoading || mappingLoading || complianceLoading;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('trustFramework.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('trustFramework.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddIssuer(true)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
          {t('trustFramework.addTrustedIssuer')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : issuersError ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadTrustFrameworkFailed')}</div>
      ) : (
        <>
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('trustFramework.configTitle')}</h2>
            <div className="flex items-center gap-6">
              <div className="flex-1">
                <label className="mb-2 block text-xs font-bold text-text-muted">
                  {t('trustFramework.minScoreLabel', { score: minTrustScore })}
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={minTrustScore}
                  onChange={(e) => setMinTrustScore(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="mt-1 flex justify-between text-[10px] text-text-faint">
                  <span>{t('trustFramework.scaleLow')}</span>
                  <span>{t('trustFramework.scaleMid')}</span>
                  <span>{t('trustFramework.scaleMax')}</span>
                </div>
              </div>
              <div className="w-24 text-center">
                <div className="text-3xl font-bold text-primary">{minTrustScore}</div>
                <div className="text-[10px] font-bold text-text-faint">{t('trustFramework.minimum')}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>verified_user</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('trustFramework.trustedIssuers')}</h2>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-text-muted">{issuers.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {issuers.map((issuer) => (
                <div key={issuer.did} className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex size-10 items-center justify-center rounded-lg ${typeBg[issuer.type] ?? 'bg-slate-100'}`}>
                        <span className={`material-symbols-outlined ${typeColor[issuer.type] ?? 'text-text-muted'}`} style={{ fontSize: 20 }}>
                          {typeIcons[issuer.type] ?? 'business'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-text-main">{issuer.name}</p>
                        <p className="text-[10px] text-text-faint">{issuer.type}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeIssuer(issuer.did)}
                      className="flex size-8 items-center justify-center rounded-lg text-text-faint transition-colors hover:bg-red-50 hover:text-red-500"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                    </button>
                  </div>

                  <div className="mt-3 font-mono text-[10px] text-text-faint">{issuer.did}</div>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-text-faint">{t('trustFramework.trustScore')}</span>
                        <span className="font-bold text-text-main">{issuer.trustScore}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full ${
                            issuer.trustScore >= 80 ? 'bg-emerald-500' : issuer.trustScore >= 60 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${issuer.trustScore}%` }}
                        />
                      </div>
                    </div>
                    <Badge variant={issuer.trustScore >= minTrustScore ? 'success' : 'warning'}>
                      {issuer.trustScore >= minTrustScore ? t('common:status.trusted') : t('common:status.belowThreshold')}
                    </Badge>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {issuer.schemas.map((s) => (
                      <span key={s} className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>schema</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('trustFramework.schemaMapping')}</h2>
            </div>
            <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-slate-50/50">
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('trustFramework.schemaTable.schema')}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('trustFramework.schemaTable.issuers')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(schemaIssuerMapping ?? []).map((mapping) => (
                    <tr key={mapping.schema} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>description</span>
                          <span className="text-sm font-bold text-text-main">{mapping.schema}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {mapping.issuers.map((issuer) => (
                            <span key={issuer} className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-primary">{issuer}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>security</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('trustFramework.complianceVCs')}</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {(complianceVCs ?? []).map((vc) => (
                <div key={vc.type} className="flex items-start gap-3 rounded-xl border border-border-subtle bg-white p-4 shadow-sm">
                  <div className={`flex size-8 items-center justify-center rounded-lg ${vc.required ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                    <span className={`material-symbols-outlined ${vc.required ? 'text-emerald-500' : 'text-text-faint'}`} style={{ fontSize: 18 }}>
                      {vc.required ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-text-main">{vc.type}</p>
                      {vc.required && <Badge variant="success" dot={false}>{t('common:status.required')}</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-text-faint">{vc.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Modal open={showAddIssuer} onClose={() => setShowAddIssuer(false)} title={t('trustFramework.modal.title')}>
        <div className="space-y-5">
          <FormField
            label={t('trustFramework.modal.didLabel')}
            placeholder={t('trustFramework.modal.didPlaceholder')}
            value={newIssuerDid}
            onChange={(e) => setNewIssuerDid((e.target as HTMLInputElement).value)}
          />
          <FormField
            label={t('trustFramework.modal.nameLabel')}
            placeholder={t('trustFramework.modal.namePlaceholder')}
            value={newIssuerName}
            onChange={(e) => setNewIssuerName((e.target as HTMLInputElement).value)}
          />
          <div className="flex justify-end gap-3">
            <button className="btn-secondary" onClick={() => setShowAddIssuer(false)}>{t('common:actions.cancel')}</button>
            <button
              className="btn-primary"
              disabled={adding}
              onClick={handleAddIssuer}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
              {adding ? t('trustFramework.modal.adding') : t('trustFramework.modal.add')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
